// src/components/AddDeliveryDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  MenuItem,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Collapse
} from '@mui/material';
import { Close, Schedule, Edit, Keyboard } from '@mui/icons-material';
import { DeliveryRecord, DeliveryPlatform } from '../types';
import { addDelivery, updateDelivery, getAllPlatforms, getAllDeliveries } from '../services/dataService';
import { trackAnalyticsEvent } from '../services/firebase';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  editData?: DeliveryRecord;
  simpleMode?: boolean;
}

// 時・分の選択肢
const hours = Array.from({ length: 24 }, (_, i) => i);
const minutes = Array.from({ length: 12 }, (_, i) => i * 5);

function AddDeliveryDialog({ open, onClose, onSuccess, onShowSnackbar, editData, simpleMode = false }: Props) {
  const [date, setDate] = useState('');
  const [platform, setPlatform] = useState('');
  const [totalRevenue, setTotalRevenue] = useState('');
  const [deliveryCount, setDeliveryCount] = useState('');
  
  // 入力モード: 'picker' (時計) or 'select' (セレクト) or 'manual' (手入力)
  const [inputMode, setInputMode] = useState<'picker' | 'select' | 'manual'>('select');
  const [timeEnabled, setTimeEnabled] = useState(!simpleMode);
  
  // ピッカー用 & 手入力用
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  
  // セレクト用
  const [startHour, setStartHour] = useState(9);
  const [startMinute, setStartMinute] = useState(0);
  const [endHour, setEndHour] = useState(18);
  const [endMinute, setEndMinute] = useState(0);
  
  const [breakMinutes, setBreakMinutes] = useState('');
  const [platformWorkingHours, setPlatformWorkingHours] = useState('');
  const [platformWorkingMinutes, setPlatformWorkingMinutes] = useState('');
  const [notes, setNotes] = useState('');
  const [platforms, setPlatforms] = useState<DeliveryPlatform[]>([]);
  const [loading, setLoading] = useState(false);
  
  // 前回の記録
  const [lastRecord, setLastRecord] = useState<DeliveryRecord | null>(null);

  useEffect(() => {
    loadPlatforms();
    loadLastRecord();
  }, []);

  useEffect(() => {
    if (editData) {
      setTimeEnabled(Boolean(editData.startTime || editData.endTime || editData.workingMinutes));
      setDate(editData.date);
      setPlatform(editData.platform);
      setTotalRevenue(editData.totalRevenue.toString());
      setDeliveryCount(editData.deliveryCount.toString());
      setBreakMinutes((editData.breakMinutes || 0).toString());
      setNotes(editData.notes || '');
      
      // 時刻を両方の形式で設定
      if (editData.startTime) {
        setStartTime(editData.startTime);
        const [h, m] = editData.startTime.split(':').map(Number);
        setStartHour(h);
        setStartMinute(m);
      }
      if (editData.endTime) {
        setEndTime(editData.endTime);
        const [h, m] = editData.endTime.split(':').map(Number);
        setEndHour(h);
        setEndMinute(m);
      }
    } else {
      setDate(new Date().toISOString().split('T')[0]);
      setTimeEnabled(!simpleMode);
      setStartTime(simpleMode ? '' : '09:00');
      setEndTime(simpleMode ? '' : '18:00');
      setStartHour(9);
      setStartMinute(0);
      setEndHour(18);
      setEndMinute(0);
      setBreakMinutes('0');
      setPlatformWorkingHours('');
      setPlatformWorkingMinutes('');
    }
  }, [editData, open, simpleMode]);

  useEffect(() => {
    if (editData?.workingMinutes && editData.workingMinutes > 0) {
      setPlatformWorkingHours(Math.floor(editData.workingMinutes / 60).toString());
      setPlatformWorkingMinutes((editData.workingMinutes % 60).toString());
    } else if (editData?.startTime && editData?.endTime) {
      const minutes = getRangeWorkingMinutes(editData.startTime, editData.endTime, editData.breakMinutes || 0);
      setPlatformWorkingHours(Math.floor(minutes / 60).toString());
      setPlatformWorkingMinutes((minutes % 60).toString());
    }
  }, [editData, open]);

  // セレクト値が変更されたらピッカー値も更新
  useEffect(() => {
    if (!timeEnabled) return;
    setStartTime(`${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`);
  }, [startHour, startMinute, timeEnabled]);

  useEffect(() => {
    if (!timeEnabled) return;
    setEndTime(`${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`);
  }, [endHour, endMinute, timeEnabled]);

  // ピッカー値が変更されたらセレクト値も更新
  const handleStartTimeChange = (value: string) => {
    setStartTime(value);
    if (value && value.includes(':')) {
      const [h, m] = value.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
        setStartHour(h);
        setStartMinute(m);
      }
    }
  };

  const handleEndTimeChange = (value: string) => {
    setEndTime(value);
    if (value && value.includes(':')) {
      const [h, m] = value.split(':').map(Number);
      if (!isNaN(h) && !isNaN(m) && h >= 0 && h < 24 && m >= 0 && m < 60) {
        setEndHour(h);
        setEndMinute(m);
      }
    }
  };

  const loadPlatforms = async () => {
    try {
      const data = await getAllPlatforms();
      setPlatforms(data.filter((p: DeliveryPlatform) => p.isActive));
    } catch (error) {
      console.error('プラットフォーム読み込みエラー:', error);
    }
  };

  const loadLastRecord = async () => {
    try {
      const deliveries = await getAllDeliveries();
      if (deliveries.length > 0) {
        setLastRecord(deliveries[0]); // 最新の記録
      }
    } catch (error) {
      console.error('前回記録の読み込みエラー:', error);
    }
  };

  // 前回の記録を使用
  const useLastRecord = () => {
    if (!lastRecord) return;
    setTimeEnabled(true);
    
    setStartTime(lastRecord.startTime || '');
    setEndTime(lastRecord.endTime || '');
    setBreakMinutes((lastRecord.breakMinutes || 0).toString());
    const lastWorkingMinutes = lastRecord.workingMinutes || getRangeWorkingMinutes(
      lastRecord.startTime || '',
      lastRecord.endTime || '',
      lastRecord.breakMinutes || 0
    );
    setPlatformWorkingHours(lastWorkingMinutes > 0 ? Math.floor(lastWorkingMinutes / 60).toString() : '');
    setPlatformWorkingMinutes(lastWorkingMinutes > 0 ? (lastWorkingMinutes % 60).toString() : '');
    
    // セレクト値も更新
    if (lastRecord.startTime) {
      const [h, m] = lastRecord.startTime.split(':').map(Number);
      setStartHour(h);
      setStartMinute(m);
    }
    if (lastRecord.endTime) {
      const [h, m] = lastRecord.endTime.split(':').map(Number);
      setEndHour(h);
      setEndMinute(m);
    }
    
    onShowSnackbar('前回の時間を適用しました', 'success');
  };

  const getRangeWorkingMinutes = (start: string, end: string, breakMins: number) => {
    if (!start || !end || !start.includes(':') || !end.includes(':')) return 0;
    const [startH, startM] = start.split(':').map(Number);
    const [endH, endM] = end.split(':').map(Number);
    if ([startH, startM, endH, endM].some(Number.isNaN)) return 0;

    const startTotal = startH * 60 + startM;
    let endTotal = endH * 60 + endM;
    if (endTotal <= startTotal) {
      endTotal += 24 * 60;
    }

    return Math.max(0, endTotal - startTotal - breakMins);
  };

  const getPlatformWorkingMinutes = () => {
    const hoursValue = parseInt(platformWorkingHours || '0');
    const minutesValue = parseInt(platformWorkingMinutes || '0');
    if (Number.isNaN(hoursValue) || Number.isNaN(minutesValue)) return NaN;
    return hoursValue * 60 + minutesValue;
  };

  const calculateWorkingTime = () => {
    const breakMins = parseInt(breakMinutes || '0');
    const workingMins = getRangeWorkingMinutes(startTime, endTime, breakMins);
    const directWorkingMins = getPlatformWorkingMinutes();
    
    const hours = Math.floor(workingMins / 60);
    const mins = workingMins % 60;
    const directHours = Math.floor(directWorkingMins / 60);
    const directMins = directWorkingMins % 60;
    
    return `時間帯: ${hours}時間${mins}分 / プラットフォーム稼働: ${directHours}時間${directMins}分`;
  };

  const handleSubmit = async () => {
    // バリデーション
    if (!date || !platform || !totalRevenue || !deliveryCount) {
      onShowSnackbar('必須項目を入力してください', 'error');
      return;
    }

    const revenue = parseInt(totalRevenue);
    const count = parseInt(deliveryCount);
    const breakMins = parseInt(breakMinutes || '0');
    const platformHoursValue = parseInt(platformWorkingHours || '0');
    const platformMinutesValue = parseInt(platformWorkingMinutes || '0');
    const directWorkingMins = getPlatformWorkingMinutes();
    const hasTimeInput = timeEnabled && Boolean(startTime || endTime || breakMins > 0 || directWorkingMins > 0);

    if (
      Number.isNaN(revenue) ||
      Number.isNaN(count) ||
      Number.isNaN(breakMins) ||
      Number.isNaN(platformHoursValue) ||
      Number.isNaN(platformMinutesValue) ||
      Number.isNaN(directWorkingMins) ||
      revenue < 0 ||
      count < 0 ||
      breakMins < 0 ||
      platformHoursValue < 0 ||
      platformMinutesValue < 0 ||
      directWorkingMins < 0
    ) {
      onShowSnackbar('正しい数値を入力してください', 'error');
      return;
    }

    let rangeWorkingMins = 0;
    let workingMins: number | undefined = undefined;

    if (hasTimeInput) {
      if (!startTime || !endTime) {
        onShowSnackbar('時間を入力する場合は開始・終了時刻を入力してください', 'error');
        return;
      }

      rangeWorkingMins = getRangeWorkingMinutes(startTime, endTime, breakMins);

      if (rangeWorkingMins <= 0) {
        onShowSnackbar('開始・終了時刻と休憩時間を確認してください', 'error');
        return;
      }
    }

    if (platformMinutesValue >= 60) {
      onShowSnackbar('分は0〜59で入力してください', 'error');
      return;
    }

    if (hasTimeInput && directWorkingMins > rangeWorkingMins) {
      onShowSnackbar('プラットフォーム稼働時間が時間帯の実働時間を超えています', 'error');
      return;
    }

    if (hasTimeInput) {
      workingMins = directWorkingMins > 0 ? directWorkingMins : rangeWorkingMins;
    }

    const preserveHiddenTime = simpleMode && Boolean(editData) && !hasTimeInput;

    setLoading(true);

    try {
      const deliveryData: Omit<DeliveryRecord, 'id'> = {
        date,
        platform,
        totalRevenue: revenue,
        deliveryCount: count,
        startTime: hasTimeInput ? startTime : preserveHiddenTime ? (editData?.startTime || '') : '',
        endTime: hasTimeInput ? endTime : preserveHiddenTime ? (editData?.endTime || '') : '',
        breakMinutes: hasTimeInput ? breakMins : preserveHiddenTime ? (editData?.breakMinutes || 0) : 0,
        workingMinutes: hasTimeInput ? (workingMins || 0) : preserveHiddenTime ? (editData?.workingMinutes || 0) : 0,
        notes,
        createdAt: Date.now()
      };

      if (editData && editData.id) {
        await updateDelivery(editData.id, deliveryData);
        trackAnalyticsEvent('delivery_record_update', { simple_mode: simpleMode });
        onShowSnackbar('記録を更新しました');
      } else {
        await addDelivery(deliveryData);
        trackAnalyticsEvent('delivery_record_create', { simple_mode: simpleMode });
        onShowSnackbar('記録を追加しました');
      }

      onSuccess();
      handleClose();
    } catch (error) {
      onShowSnackbar('保存に失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDate('');
    setPlatform('');
    setTotalRevenue('');
    setDeliveryCount('');
    setStartTime('');
    setEndTime('');
    setStartHour(9);
    setStartMinute(0);
    setEndHour(18);
    setEndMinute(0);
    setBreakMinutes('');
    setPlatformWorkingHours('');
    setPlatformWorkingMinutes('');
    setTimeEnabled(!simpleMode);
    setNotes('');
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#101512',
          color: 'white',
          borderRadius: '20px',
          border: '1px solid rgba(255,255,255,0.08)',
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)', bgcolor: '#151a17' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="bold">
            {simpleMode ? (editData ? 'シンプル記録を編集' : 'シンプル記録') : (editData ? '配達記録を編集' : '配達記録を追加')}
          </Typography>
          <IconButton onClick={handleClose} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)' }}>
            <Typography fontSize={15} color="#b7c5bf">
              {simpleMode
                ? '売上と件数だけでサッと保存できます。時間入力や時給管理は通常モードで使えます。'
                : '売上・件数・稼働時間だけ入力すれば、時給や目標達成率は自動で集計されます。'}
            </Typography>
          </Box>
          <TextField
            label="日付"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          />

          <TextField
            select
            label="プラットフォーム"
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          >
            {platforms.map((p) => (
              <MenuItem key={p.id} value={p.name}>
                {p.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="売上(円)"
            type="number"
            value={totalRevenue}
            onChange={(e) => setTotalRevenue(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          />

          <TextField
            label="配達件数"
            type="number"
            value={deliveryCount}
            onChange={(e) => setDeliveryCount(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          />

          {!simpleMode && (
          <>
          <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: timeEnabled ? 'rgba(16,185,129,0.08)' : 'rgba(255,255,255,0.045)', border: timeEnabled ? '1px solid rgba(16,185,129,0.18)' : '1px solid rgba(255,255,255,0.08)' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography fontSize={15} fontWeight="bold" color={timeEnabled ? '#6EE7B7' : '#d5ddd8'}>
                  時間入力は任意です
                </Typography>
                <Typography fontSize={13} color="#9ca7a0">
                  売上だけ管理する場合は入力しなくてOKです。時給も見たい場合だけ開いてください。
                </Typography>
              </Box>
              <Button
                size="small"
                variant={timeEnabled ? 'outlined' : 'contained'}
                onClick={() => {
                  const next = !timeEnabled;
                  setTimeEnabled(next);
                  if (!next) {
                    setStartTime('');
                    setEndTime('');
                    setBreakMinutes('0');
                    setPlatformWorkingHours('');
                    setPlatformWorkingMinutes('');
                  } else if (!startTime && !endTime) {
                    setStartTime(`${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`);
                    setEndTime(`${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`);
                  }
                }}
                sx={{
                  flexShrink: 0,
                  bgcolor: timeEnabled ? 'transparent' : '#10B981',
                  color: timeEnabled ? '#6EE7B7' : '#07120d',
                  borderColor: 'rgba(16,185,129,0.45)',
                  '&:hover': {
                    bgcolor: timeEnabled ? 'rgba(16,185,129,0.08)' : '#34d399',
                    borderColor: '#10B981'
                  }
                }}
              >
                {timeEnabled ? '閉じる' : '時間も入力'}
              </Button>
            </Box>
          </Box>

          <Collapse in={timeEnabled} timeout="auto" unmountOnExit>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* 入力モード切り替え */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: '#aaa' }}>
                時刻入力方法
              </Typography>
              {lastRecord && !editData && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={useLastRecord}
                  sx={{
                    color: '#10B981',
                    borderColor: 'rgba(16,185,129,0.45)',
                    bgcolor: 'rgba(16,185,129,0.08)',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    px: 1,
                    py: 0.5,
                    '&:hover': {
                      borderColor: '#10B981',
                      bgcolor: 'rgba(16,185,129,0.14)'
                    }
                  }}
                >
                  前回の時間を使用
                </Button>
              )}
            </Box>
            <ToggleButtonGroup
              value={inputMode}
              exclusive
              onChange={(e, value) => value && setInputMode(value)}
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  color: '#aaa',
                  borderColor: '#444',
                  fontSize: '0.75rem',
                  '&.Mui-selected': {
                    bgcolor: '#10B981',
                    color: 'white',
                    '&:hover': { bgcolor: '#059669' }
                  }
                }
              }}
            >
              <ToggleButton value="select">
                <Edit sx={{ mr: 0.5, fontSize: 16 }} />
                選択
              </ToggleButton>
              <ToggleButton value="manual">
                <Keyboard sx={{ mr: 0.5, fontSize: 16 }} />
                手入力
              </ToggleButton>
              <ToggleButton value="picker">
                <Schedule sx={{ mr: 0.5, fontSize: 16 }} />
                時計
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* 時刻入力エリア */}
          {inputMode === 'picker' ? (
            // 時計ピッカー
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="開始時刻"
                type="time"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                }}
              />

              <TextField
                label="終了時刻"
                type="time"
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                }}
              />
            </Box>
          ) : inputMode === 'manual' ? (
            // 手入力
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="開始時刻"
                placeholder="09:00"
                value={startTime}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                fullWidth
                inputProps={{
                  pattern: "[0-9]{2}:[0-9]{2}",
                }}
                helperText="例: 09:00"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                  '& .MuiFormHelperText-root': { color: '#666' },
                }}
              />

              <TextField
                label="終了時刻"
                placeholder="18:00"
                value={endTime}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                fullWidth
                inputProps={{
                  pattern: "[0-9]{2}:[0-9]{2}",
                }}
                helperText="例: 18:00"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                  '& .MuiFormHelperText-root': { color: '#666' },
                }}
              />
            </Box>
          ) : (
            // セレクトボックス
            <>
              <Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                  開始時刻
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    select
                    value={startHour}
                    onChange={(e) => setStartHour(Number(e.target.value))}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#666' },
                      },
                    }}
                  >
                    {hours.map((h) => (
                      <MenuItem key={h} value={h}>{h}時</MenuItem>
                    ))}
                  </TextField>
                  
                  <TextField
                    select
                    value={startMinute}
                    onChange={(e) => setStartMinute(Number(e.target.value))}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#666' },
                      },
                    }}
                  >
                    {minutes.map((m) => (
                      <MenuItem key={m} value={m}>{m}分</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ color: '#aaa', mb: 1 }}>
                  終了時刻
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    select
                    value={endHour}
                    onChange={(e) => setEndHour(Number(e.target.value))}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#666' },
                      },
                    }}
                  >
                    {hours.map((h) => (
                      <MenuItem key={h} value={h}>{h}時</MenuItem>
                    ))}
                  </TextField>
                  
                  <TextField
                    select
                    value={endMinute}
                    onChange={(e) => setEndMinute(Number(e.target.value))}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: 'white',
                        '& fieldset': { borderColor: '#444' },
                        '&:hover fieldset': { borderColor: '#666' },
                      },
                    }}
                  >
                    {minutes.map((m) => (
                      <MenuItem key={m} value={m}>{m}分</MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
            </>
          )}

          <TextField
            label="休憩時間(分)"
            type="number"
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          />

          <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.18)' }}>
            <Typography fontSize={15} fontWeight="bold" color="#93C5FD" sx={{ mb: 0.75 }}>
              プラットフォーム稼働時間
            </Typography>
            <Typography fontSize={13} color="#b7c5bf" sx={{ mb: 1.25 }}>
              このサービスを実際にオンライン・稼働していた合計時間を入力してください。複数サービスを同時に使った日は、サービスごとに別々の稼働時間を入れると時給が正確になります。
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <TextField
                label="時間"
                type="number"
                value={platformWorkingHours}
                onChange={(e) => setPlatformWorkingHours(e.target.value)}
                inputProps={{ min: 0 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                }}
              />
              <TextField
                label="分"
                type="number"
                value={platformWorkingMinutes}
                onChange={(e) => setPlatformWorkingMinutes(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#444' },
                    '&:hover fieldset': { borderColor: '#666' },
                  },
                  '& .MuiInputLabel-root': { color: '#aaa' },
                }}
              />
            </Box>
          </Box>

          {startTime && endTime && (
            <Typography variant="body2" sx={{ color: '#10B981' }}>
              {calculateWorkingTime()}
            </Typography>
          )}
            </Box>
          </Collapse>
          </>
          )}

          <TextField
            label="メモ"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': { borderColor: '#444' },
                '&:hover fieldset': { borderColor: '#666' },
              },
              '& .MuiInputLabel-root': { color: '#aaa' },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2, borderTop: '1px solid rgba(255,255,255,0.08)', bgcolor: '#151a17' }}>
        <Button onClick={handleClose} sx={{ color: '#aaa', height: 44, px: 2 }}>
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            height: 44,
            px: 3,
            bgcolor: '#10B981',
            color: '#07120d',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#34d399', boxShadow: 'none' },
            '&:disabled': { bgcolor: '#26342e', color: '#718078' }
          }}
        >
          {loading ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddDeliveryDialog;

